import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ApiError,
  Button,
  ErrorAlert,
  Skeleton,
  buttonClass,
  formatUsd,
  networkLabel,
  useDocumentTitle,
} from "@unizpay/ui";
import { type ReactNode, useState } from "react";
import { checkoutApi } from "./api";
import { CheckoutShell } from "./components/CheckoutShell";
import { type MethodChoice, MethodStep } from "./components/MethodStep";
import { Outcome, SuccessRedirect } from "./components/Outcome";
import { PayStep } from "./components/PayStep";
import { ProgressStep } from "./components/ProgressStep";
import { safeUrl, toppedUp } from "./session";
import type { IssuedAddress, PaymentStatus } from "./types";

const POLL_MS = 5000;
/** While the payer is sending, or the payment is confirming: each step shown at once. */
const FAST_POLL_MS = 1500;
const STOP_POLLING: ReadonlySet<PaymentStatus> = new Set(["settled", "settled_raw", "frozen", "canceled"]);
/**
 * Paid, as far as the payer is concerned: confirmed on the network. Converting and delivering
 * to the merchant happens after, between UnizPay and the merchant.
 */
const COMPLETE: ReadonlySet<PaymentStatus> = new Set(["confirmed", "swapping", "settled", "settled_raw"]);

function FlowSkeleton({ label }: { label: string }) {
  return (
    <output aria-label={label} className="block space-y-5">
      <Skeleton className="h-6 w-48" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-11" />
        <Skeleton className="h-11" />
      </div>
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-11 w-full" />
    </output>
  );
}

/**
 * Hosted payment page, in three steps: choose a network and coin → send the payment →
 * follow it to completion. A client of the public API using the payment's checkout token.
 */
export function CheckoutPage({ id }: { id: string }) {
  // The checkout token travels in the URL fragment, which browsers never send to servers.
  const [cs] = useState(() => window.location.hash.slice(1));
  const [picked, setPicked] = useState<IssuedAddress | null>(null);
  const [choosing, setChoosing] = useState(false);

  const query = useQuery({
    queryKey: ["checkout", id],
    queryFn: () => checkoutApi.payment(id, cs),
    enabled: cs.length > 0,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && STOP_POLLING.has(data.status)) return false;
      const sending = data?.status === "awaiting_payment" && data.addresses.length > 0;
      return data && (sending || data.status === "detected" || toppedUp(data)) ? FAST_POLL_MS : POLL_MS;
    },
    refetchIntervalInBackground: true,
    retry: (count, err) =>
      !(err instanceof ApiError && (err.status === 404 || err.status === 401)) && count < 3,
  });
  const methods = useQuery({
    queryKey: ["checkout", id, "methods"],
    queryFn: () => checkoutApi.methods(id, cs),
    enabled: cs.length > 0 && query.data?.status === "awaiting_payment",
    staleTime: 60_000,
  });

  const issue = useMutation({
    mutationFn: (m: MethodChoice) => checkoutApi.issueAddress(id, cs, m.token, m.network),
    onSuccess: (res) => {
      setPicked(res);
      setChoosing(false);
    },
  });

  const session = query.data;
  useDocumentTitle(session ? `Pay ${session.merchant_name}` : "Checkout");

  if (query.isPending && cs.length > 0) {
    return (
      <CheckoutShell session={undefined}>
        <FlowSkeleton label="Loading payment" />
      </CheckoutShell>
    );
  }

  if (!session) {
    // No token, a wrong token, or an unknown payment all mean the link is not valid.
    const notFound =
      cs.length === 0 ||
      (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 401));
    return (
      <CheckoutShell session={undefined}>
        {notFound ? (
          <Outcome tone="neutral" title="Payment not found">
            <p>
              This payment link is invalid or has been canceled. Please return to the merchant and try again.
            </p>
          </Outcome>
        ) : (
          <div className="space-y-4">
            <ErrorAlert error={query.error} title="Could not load this payment" />
            <Button variant="secondary" size="lg" className="w-full" onClick={() => query.refetch()}>
              Try again
            </Button>
          </div>
        )}
      </CheckoutShell>
    );
  }

  const status = session.status;
  const successUrl = safeUrl(session.success_url);
  const cancelUrl = safeUrl(session.cancel_url);
  const lastIssued = picked ?? session.addresses.at(-1) ?? null;
  const issued = choosing ? null : lastIssued;
  // Expiry is the server's call: an address issued just before the link's expiry keeps its own
  // full window to pay, so the client never times a payment out by itself.
  const awaiting = status === "awaiting_payment";
  const amount = formatUsd(session.amount, session.currency);
  const refreshQuote = (m: MethodChoice) => issue.mutate({ token: m.token, network: m.network });
  // A failed refresh keeps the last known state on screen; polling continues in the background.
  const reconnecting = query.isError;

  let body: ReactNode;

  if (COMPLETE.has(status)) {
    body = (
      <Outcome tone="ok" title="Payment complete">
        <p>
          {lastIssued
            ? `${lastIssued.pay_amount} ${lastIssued.token} on ${networkLabel(lastIssued.network)} to ${session.merchant_name} is confirmed.`
            : `Your payment of ${amount} to ${session.merchant_name} is confirmed.`}
        </p>
        {successUrl ? <SuccessRedirect url={successUrl} /> : <p>You can close this page.</p>}
      </Outcome>
    );
  } else if (status === "detected" || toppedUp(session)) {
    body = <ProgressStep issued={lastIssued} />;
  } else if (status === "late") {
    body = (
      <Outcome tone="warn" title="Payment received after expiry">
        <p>
          Your payment arrived after this payment closed. It is still being processed and will be delivered to{" "}
          {session.merchant_name}. Contact them if you need confirmation of your order.
        </p>
      </Outcome>
    );
  } else if (status === "expired") {
    body = (
      <Outcome tone="neutral" title="This payment has expired">
        <p>
          Don't send funds to it. Already sent? Keep this page open: late payments are still detected and
          delivered to {session.merchant_name}.
        </p>
        {cancelUrl && (
          <a href={cancelUrl} className={buttonClass("secondary", "lg")}>
            Return to {session.merchant_name}
          </a>
        )}
      </Outcome>
    );
  } else if (status === "frozen") {
    body = (
      <Outcome tone="danger" title="Payment on hold">
        <p>
          The coin's issuer has blocked the deposit address, so these funds cannot be moved. Please contact{" "}
          {session.merchant_name} for help. Do not send further funds.
        </p>
      </Outcome>
    );
  } else if (status === "reversed") {
    body = (
      <Outcome tone="danger" title="Payment reversed">
        <p>
          The network reversed the transfer for this payment, so it is no longer paid. Check your wallet, and
          contact {session.merchant_name} if the funds left it.
        </p>
      </Outcome>
    );
  } else if (status === "canceled") {
    body = (
      <Outcome tone="neutral" title="No longer active">
        <p>This payment link is no longer active. Do not send funds.</p>
      </Outcome>
    );
  } else if (status === "underpaid" && lastIssued) {
    // The session's copy of the address carries what is still due.
    const owed = session.addresses.find((a) => a.address === lastIssued.address) ?? lastIssued;
    body = (
      <PayStep
        issued={owed}
        onBack={null}
        onRefreshQuote={() => refreshQuote(owed)}
        refreshing={issue.isPending}
      />
    );
  } else if (!issued) {
    body = methods.isPending ? (
      <FlowSkeleton label="Loading payment methods" />
    ) : methods.isError ? (
      <div className="space-y-3">
        <ErrorAlert error={methods.error} title="Could not load payment methods" />
        <Button variant="secondary" size="lg" className="w-full" onClick={() => methods.refetch()}>
          Try again
        </Button>
      </div>
    ) : (
      <MethodStep
        methods={methods.data.data}
        initial={lastIssued}
        onContinue={(m) => issue.mutate(m)}
        submitting={issue.isPending}
        error={issue.error}
      />
    );
  } else {
    body = (
      <>
        <PayStep
          issued={issued}
          onBack={() => {
            issue.reset();
            setChoosing(true);
          }}
          onRefreshQuote={() => refreshQuote(issued)}
          refreshing={issue.isPending}
        />
        <ErrorAlert error={issue.error} title="Could not get a new amount" />
      </>
    );
  }

  return (
    <CheckoutShell session={session} backUrl={awaiting ? cancelUrl : null}>
      <p className="sr-only" aria-live="polite">
        Payment status: {status.replace(/_/g, " ")}
      </p>
      {reconnecting && (
        <output className="mb-4 flex items-center gap-2 text-[0.8125rem] text-fg-muted">
          <span className="size-1.5 animate-pulse rounded-full bg-warn" aria-hidden />
          Reconnecting…
        </output>
      )}
      {body}
    </CheckoutShell>
  );
}
